import { MeetingTitleInfo } from '@components/meeting-details/component/meetingTitleInfo';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import barcodePng from '@assets/images/barcode.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { IMeetingDetails } from '@components/meeting-details';
import { Modal } from '@components/common/modal/modal';
import { AddQuickUserModal } from './components/addQuickUser/addQuickUserForm';
import TextInput from '@components/common/formControl/textInput/textInput';
import {
	cityChapterMeet,
	defaultInitialFeedback,
	EI_MEET_LINKS,
	linkCaptureVideosMeet,
	feedbackSrtuctureMeets,
} from '.';
import { useFormik } from 'formik';
import { useAddFeedbackMutation } from '@services/hooks/meetings/useUpdateFeedbackForm';
import { ToastType, useTosterStore } from '@store/toster';
import { useGetParticipantsQuery } from '@services/hooks/meetings/useGetParticipants';
import { useGetRBDMParticipants } from '@services/hooks/meetings/useGetRBDMParticipants';
import { Wrapper } from '@components/common/Wrapper/wrapper';
import { SubmitButton } from '@components/common';
import { SummaryBoxCard } from './components/summaryCard/summaryBoxCard';
import { useMeetingsListQuery } from '@services/hooks/meetings/useMeetingsListQuery';
import { useUpdateMeetingStatus } from '@services/hooks/meetings/useUpdateMeetingStatus';
import { getDateByRange } from '@helper/utils';
import { MeetingQrModal } from './components/meetingsQrModal/meetingQrModal';
import Checkbox from '@components/common/formControl/checkbox/checkbox';
import { useValidation } from '@hooks/validations';
import { useComponentLevelAccessCheck } from '../../authGuard/hooks/useComponentLevelAccessCheck';
import {
	FCC_PRODUCT_AWARENESS_MEET,
	IsEISalesGroupCode,
	MEDIA_VALIDATION_MESSAGES,
	MISS_CALL_NUMBER_API_STATUS,
	MISS_CALL_NUMBER_ERROR_MESSAGE,
	OFFICE_BEARER_MEET,
} from '@config/constant';
import { useGetRBDMMeetingQuery } from '@services/hooks/meetings/useGetRBDMMeetingQuery';
import { useGetRxDb } from '@hooks/getRxdbData';
import { ProfileData } from '@services/hooks/profile';
import { usePresignedURLQuery } from '@services/hooks/enduser-details/usePresignedURLQuery';
import { useUploadSiteImagesMutation } from '@services/hooks/site-leads/useUploadSiteImagesMutation';
import { useUploadMeetingImagesMutation } from '@services/hooks/meetings/useUploadMeetingImagesMutation';
import { UploadPhotosVideos } from '@components/common/uploadPhotosVideos/uploadPhotosVideos';
import { useMediaUpload } from '@hooks/usemMediaUpload';
import { useIsImpersonationLoginUser } from '../../authGuard/hooks/useIsImpersonationLoginUser';

export const TodayMeetingDetails = (): ReactElement => {
	const { state } = useLocation();
	const { result: profile } = useGetRxDb<ProfileData>('profile');
	const userRole = profile[0]?.usertype_c;
	const isRBDM = userRole === 'RBDM';
	const { data, isFetching, error } = useGetRBDMMeetingQuery(state?.id);

	const { data: rbdmParticipants, error: rbdmParticipantsError } = useGetRBDMParticipants(state?.id);
	const {
		data: regularParticipants,
		error: regularParticipantsError,
		refetch,
	} = useGetParticipantsQuery({ id: state?.id });

	const participants = isRBDM ? rbdmParticipants : regularParticipants;
	const participantsError = isRBDM ? rbdmParticipantsError : regularParticipantsError;
	const [feedbackCheck, setFeedbackCheck] = useState(false);
	const navigate = useNavigate();
	const { refetch: meetingsRefetch } = useMeetingsListQuery();
	const { mutate: saveMeetingImages } = useUploadMeetingImagesMutation();

	const { handleUploadMedia } = useMediaUpload();
	const commonProps: IMeetingDetails = {
		data: data,
		isFetching,
		error,
	};

	const { setToaster } = useTosterStore();
	const { FeedbackValidationSchema } = useValidation();
	const [showQuickAddUser, setShowQuickAddUser] = useState(false);
	const { mutate, isPending } = useAddFeedbackMutation(state?.id);
	const { mutate: meetingStatusUpdate, isPending: meetingsUpdatePending } = useUpdateMeetingStatus(state?.id);
	const [showQrCode, setShowQrCode] = useState<boolean>(false);
	const [feedbackLink, setFeedbackLink] = useState<string>('');
	const [isFeedbackPending, setIsFeedbackPending] = useState<boolean>(false);
	const [isFeedbackRequired, setIsFeedbackRequired] = useState<boolean>(false);

	const participantsArray = Array.isArray(participants) ? participants : [];
	const invitedParticipants = participantsArray.filter((item) => !item.non_invited_c && !item.attendance_c);
	const attendedNon = participantsArray.filter((item) => item.non_invited_c && item.attendance_c);
	const attendedInv = participantsArray.filter((item) => !item.non_invited_c && item.attendance_c);
	const checkImrTodayMeetingPermission = useComponentLevelAccessCheck('todays-meeting');
	const { mutate: mutatePresignedURL, data: getSiteImagesPresignedUrl } = usePresignedURLQuery();
	const { mutate: putSiteImagesData, isPending: isPutSiteImagePending } =
		useUploadSiteImagesMutation(getSiteImagesPresignedUrl);

	const shouldShowMeetingImages = linkCaptureVideosMeet?.includes(data?.meeting_type_c ?? '') ?? false;
	const isImpersonationLoginUser = useIsImpersonationLoginUser();

	const formik = useFormik({
		initialValues: defaultInitialFeedback,
		onSubmit: () => {
			handleSubmitForm();
		},
		validationSchema: FeedbackValidationSchema,
	});

	useEffect(() => {
		const intervalId = setInterval(() => {
			if (isRBDM) {
				// For RBDM users, we don't need to refetch as the data comes from the meeting query
				// which already has its own refetch mechanism
			} else {
				refetch?.();
			}
		}, 30000); // Fetch participants for every 30 sec

		return (): void => clearInterval(intervalId);
	}, [refetch, isRBDM]);

	useEffect(() => {
		const files: File[] = formik.values.meetingPhotos || [];
		if (!files.length || !shouldShowMeetingImages) return;

		const folderName = 'meeting';
		const payload = files.map((file) => ({
			filename: file.name,
			folder: folderName,
		}));

		mutatePresignedURL(payload);
	}, [formik.values.meetingPhotos, shouldShowMeetingImages]);

	useEffect(() => {
		if (data?.meeting_type_c) {
			formik.setFieldValue('meetingType', data.meeting_type_c);
			const meetingType = data?.meeting_type_c.toUpperCase() ?? '';
			if (feedbackSrtuctureMeets.some((item) => meetingType.includes(item))) {
				setIsFeedbackRequired(true);
				const meetingName = data?.name;
				const meetingSfid = data?.sfid;
				const feedbackCheck = data?.feedback_url_c ? true : false;

				setFeedbackCheck(feedbackCheck);
				const feedbackStructurUrl = process.env.FEEDBACK_STRUCTURE_URL;
				const fccMeetId = process.env.FCC_MEET_ID;
				const officeBearerMeetId = process.env.OFFICE_BEARER_MEET_ID;
				const eiOfficeBearerMeetId = process.env.EI_OFFICE_BEARER_MEET_ID;

				if (IsEISalesGroupCode.includes(profile?.[0]?.salesgroupid_c) && meetingType === OFFICE_BEARER_MEET) {
					const link =
						feedbackStructurUrl +
						'?id=' +
						eiOfficeBearerMeetId +
						`&r542a50a0918d4df9a4e7f07c9fbd78b6=${meetingName}&r19b6ca14aa4d482699fa7d29a34232aa=${meetingSfid}`;
					setFeedbackLink(link);
					setIsFeedbackPending(feedbackCheck);
				} else if (meetingType === FCC_PRODUCT_AWARENESS_MEET) {
					const link =
						feedbackStructurUrl +
						'?id=' +
						fccMeetId +
						`&r7b4208a9c8d349418a3f5020a05502b8=${meetingName}&r79bf9e90c543463c86fc0d5230a56734=${meetingSfid}`;
					setFeedbackLink(link);
					setIsFeedbackPending(feedbackCheck);
				} else if (meetingType === 'OFFICE BEARER MEET') {
					const link =
						feedbackStructurUrl +
						'?id=' +
						officeBearerMeetId +
						`&r7b4208a9c8d349418a3f5020a05502b8=${meetingName}&r79bf9e90c543463c86fc0d5230a56734=${meetingSfid}`;
					setFeedbackLink(link);
					setIsFeedbackPending(feedbackCheck);
				}
			}
		}
	}, [data?.meeting_type_c, data?.feedback_url_c]);

	const uploadMeetingImages = useCallback(
		(onSuccess: (imageUrls: string[]) => void) => {
			const files: File[] = formik.values.meetingPhotos || [];
			if (!files.length) {
				setToaster(true, ToastType.error, 'Please upload at least one meeting image/video');
				return;
			}

			putSiteImagesData(files, {
				onSuccess: () => {
					const imageUrls =
						(getSiteImagesPresignedUrl?.map((item) => item.normal_url).filter(Boolean) as string[]) || [];
					onSuccess(imageUrls);
				},
				onError: (error: Error) => {
					setToaster(true, ToastType.error, error.message);
				},
			});
		},
		[formik.values.meetingPhotos, putSiteImagesData, getSiteImagesPresignedUrl, setToaster]
	);

	const handlePostFeedbackSuccess = useCallback(
		(imageUrls: string[]) => {
			meetingStatusUpdate(
				{ status: 'List Of Meeting Conducted' },
				{
					onSuccess: () => {
						if (shouldShowMeetingImages && imageUrls.length > 0) {
							saveMeetingImages(
								{
									meeting_event_c: data?.sfid as string,
									meeting_photos: imageUrls,
								},
								{
									onSuccess: () => {
										formik.resetForm();
										navigate('/meetings/all-meetings');
										setToaster(true, ToastType.success, 'Meeting Added Successfully');
										meetingsRefetch?.();
									},
									onError: (error: Error) => {
										setToaster(true, ToastType.error, error.message);
									},
								}
							);
						} else {
							formik.resetForm();
							navigate('/meetings/all-meetings');
							setToaster(true, ToastType.success, 'Meeting Added Successfully');
							meetingsRefetch?.();
						}
					},
					onError: (error: Error) => {
						setToaster(true, ToastType.error, error.message);
					},
				}
			);
		},
		[
			meetingStatusUpdate,
			shouldShowMeetingImages,
			saveMeetingImages,
			data?.sfid,
			formik,
			navigate,
			setToaster,
			meetingsRefetch,
		]
	);

	const handleSubmitForm = useCallback(() => {
		if (attendedInv.length === 0 && attendedNon.length === 0) {
			setToaster(true, ToastType.error, 'Atleast one attendee should be in a meeting!');
			return;
		}

		const feedbackPayload = {
			obstaclesfaced_c: formik.values.obstacleFaced.trim(),
			learningfromactivities_c: formik.values.learningForm.trim(),
		};

		if (shouldShowMeetingImages) {
			uploadMeetingImages((imageUrls) => {
				mutate(feedbackPayload, {
					onSuccess: () => handlePostFeedbackSuccess(imageUrls),
					onError: (error: Error) => {
						setToaster(true, ToastType.error, error.message);
					},
				});
			});
		} else {
			mutate(feedbackPayload, {
				onSuccess: () => handlePostFeedbackSuccess([]),
				onError: (error: Error) => {
					setToaster(true, ToastType.error, error.message);
				},
			});
		}
	}, [
		attendedInv.length,
		attendedNon.length,
		formik,
		mutate,
		shouldShowMeetingImages,
		uploadMeetingImages,
		handlePostFeedbackSuccess,
		setToaster,
	]);

	const handleMediaAdd = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const folderName = 'meeting';
			const maxMediaLimit = 10;
			const maximumFileSize = 20 * 1024 * 1024;
			const allowedType = 'image / video';

			const result = handleUploadMedia(
				event,
				formik.values.meetingPhotos || [],
				maximumFileSize,
				maxMediaLimit,
				folderName,
				allowedType
			);

			if (result) {
				const { updatedFiles } = result;

				updatedFiles && formik.setFieldValue('meetingPhotos', updatedFiles);
			}
		},
		[formik, handleUploadMedia]
	);

	return (
		<>
			<Wrapper isError={error} isLoaderVisible={isFetching}>
				<section className="todays-meeting-section">
					<div className="top-content">
						<div className="container">
							<MeetingTitleInfo {...commonProps} />

							<div className="divider"></div>
							<ul className="mised-call-date-list">
								<li>
									<div className="call-history-wrapper">
										<span className="content h4">Missed Call Number</span>
										<span className="number-date">
											{data?.missedcallnumber_c ?? ' '}
											{data?.misscall_api_error_c === MISS_CALL_NUMBER_API_STATUS ? MISS_CALL_NUMBER_ERROR_MESSAGE : ''}
										</span>
									</div>
								</li>

								<li>
									<div className="call-history-wrapper">
										<span className="content h4">Missed Date Time</span>
										<span className="number-date">{data?.start_time_c ?? '--'}</span>
									</div>
								</li>
							</ul>

							{!isRBDM && !isImpersonationLoginUser && (
								<div className="qr-box">
									<span className="h4">Scan QR Code</span>
									<a
										href="/"
										aria-label="qr scan"
										className="qr-scan"
										onClick={(e) => {
											e.preventDefault();
											setShowQrCode(true);
										}}
									>
										<img src={barcodePng} alt="barcode" title="barcode" width="16" height="16" />
									</a>
								</div>
							)}
						</div>
					</div>

					<Wrapper isError={participantsError}>
						<div className="container">
							<div className="participant-card">
								<SummaryBoxCard
									cardKey={0}
									label="Invited"
									value={invitedParticipants?.length}
									path="/today-meetings/attendance-details"
									participants={participants ?? []}
									id={state?.id}
									placeHolder="Non-Attended"
									className={isRBDM ? 'disabled-card' : ''}
								/>
								<SummaryBoxCard
									cardKey={1}
									label="Attended"
									value={attendedNon?.length}
									path="/today-meetings/attendance-details"
									participants={participants ?? []}
									id={state?.id}
									placeHolder="Non-Invited"
									className={isRBDM ? 'disabled-card' : ''}
								/>
								<SummaryBoxCard
									cardKey={2}
									label="Attended"
									value={attendedInv?.length}
									path="/today-meetings/attendance-details"
									participants={participants ?? []}
									id={state?.id}
									placeHolder="Invited"
									className={isRBDM ? 'disabled-card' : ''}
								/>
							</div>

							{!isRBDM && !isImpersonationLoginUser && (
								<>
									{checkImrTodayMeetingPermission && (
										<div className="add-part-wrapper">
											<button
												type="button"
												aria-label="Add Attended (Non-Invited)"
												className="add-part-btn"
												onClick={() => setShowQuickAddUser(true)}
												disabled={isRBDM}
											>
												<span className="icon">
													<span className="icon-plus"></span>
												</span>
												<span className="content">
													Add Attended <span className="normal-text">(Non-Invited)</span>
												</span>
											</button>
										</div>
									)}
								</>
							)}

							{!isRBDM && !isImpersonationLoginUser && (
								<>
									<div className="cmn-heading">
										<span> Contractor Feedback </span>
									</div>

									<div className="">
										<form onSubmit={formik.handleSubmit}>
											<ul className="feedback-form row">
												<li className="form-control two-line-lable sm-single d-col d-col-2">
													<TextInput
														id="obstacleFaced"
														name="obstacleFaced"
														type="text"
														value={formik.values.obstacleFaced ?? ''}
														label="Obstacle faced in meeting arrangement /execution"
														placeholder="Enter Here"
														setFieldValue={formik.setFieldValue}
														error={
															formik.touched.obstacleFaced && formik.errors.obstacleFaced
																? formik.errors.obstacleFaced
																: null
														}
														onBlur={formik.handleBlur}
														required
														disabled={!checkImrTodayMeetingPermission}
													/>
												</li>

												<li className="form-control d-col d-col-2">
													<TextInput
														id="learningForm"
														name="learningForm"
														type="text"
														value={formik.values.learningForm ?? ''}
														label="Any learning from the meeting"
														placeholder="Enter Here"
														setFieldValue={formik.setFieldValue}
														onBlur={formik.handleBlur}
														error={
															formik.touched.learningForm && formik.errors.learningForm
																? formik.errors.learningForm
																: null
														}
														required
														disabled={!checkImrTodayMeetingPermission}
													/>
												</li>
											</ul>
										</form>
										{checkImrTodayMeetingPermission && (
											<div className="feedback-about-content">
												<div className="feedback-content">
													<p>
														Click link to fill form{' '}
														<a
															aria-label="feedback-form"
															href={feedbackLink ? feedbackLink : 'https://forms.office.com/r/9sfrn3RqYZ'}
															target="_blank"
															rel="noreferrer"
														>
															- User Feedback Structured Questionnaire form
														</a>
														{cityChapterMeet.some((item) => data?.meeting_type_c.includes(item)) && (
															<>
																<a
																	aria-label="feedback-form"
																	href="https://forms.office.com/r/J5MxniuAVy"
																	target="_blank"
																	rel="noreferrer"
																>
																	- Feedback - City Chapter Meet
																</a>{' '}
															</>
														)}
													</p>
												</div>
												<Checkbox
													id="terms_and_conditions"
													name="terms_and_conditions"
													checked={feedbackCheck}
													onChange={() => {
														setFeedbackCheck(!feedbackCheck);
													}}
													type="checkbox"
													label="I have filled the Structured questionnaire form"
												/>
											</div>
										)}

										{checkImrTodayMeetingPermission && !isImpersonationLoginUser && (
											<SubmitButton
												isEditing={false}
												isDisabled={
													!feedbackCheck || !formik.isValid || !formik.dirty || isRBDM || isPutSiteImagePending
												}
												handleSubmitClick={formik.handleSubmit}
												isPending={isFetching || isPending || meetingsUpdatePending || isPutSiteImagePending}
												label="Confirm Meeting Conducted"
											>
												<p className="note-content">
													Note: Please add meeting participants before{' '}
													{data?.enddate ? getDateByRange(new Date(data?.enddate), 5) : new Date().toLocaleDateString()}
												</p>
												{isFeedbackRequired && !isFeedbackPending && (
													<p className="note-content">Please fill the structured questionnaire form</p>
												)}
											</SubmitButton>
										)}
									</div>
								</>
							)}
							{shouldShowMeetingImages && (
								<>
									<div className="cmn-heading">
										<span>Meeting Photos / Videos</span>
									</div>

									<li className="d-col d-col-2">
										<UploadPhotosVideos
											fieldName="meetingPhotos"
											formik={formik}
											handleUploadMedia={handleMediaAdd}
											title="Meeting Photos / Videos"
											required
											showAddButton={!isImpersonationLoginUser}
										/>
									</li>

									<li className="d-col d-col-2">
										<span className="note-text">Note: {MEDIA_VALIDATION_MESSAGES}</span>
									</li>
								</>
							)}
						</div>
					</Wrapper>
				</section>
			</Wrapper>

			<Modal
				modalId="popupDialog"
				className="dialog dialog-lg  dialog-bottom qr-scan-wrapper quick-add-user-pop"
				modalOpen={showQuickAddUser}
				onModalClose={() => setShowQuickAddUser(false)}
			>
				<AddQuickUserModal
					dateObject={{ id: state?.id }}
					onClose={() => setShowQuickAddUser(false)}
					refetch={() => refetch?.()}
					meetingData={data}
				/>
			</Modal>
			<Modal
				modalId="metingModal"
				className="dialog dialog-lg dialog-bottom scan-popup"
				modalOpen={showQrCode}
				onModalClose={() => {
					setShowQrCode(false);
				}}
			>
				<MeetingQrModal
					onModalClose={() => {
						setShowQrCode(false);
					}}
					dataObject={data}
				/>
			</Modal>
		</>
	);
};
